import { Directive, HostBinding, Input } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { identity } from 'ramda';

import { WithDisabled } from '../helpers';

@Directive()
export abstract class BaseInputDirective implements ControlValueAccessor, WithDisabled {
  @Input() inputId: string;
  @Input() name: string;
  @Input() label: string;
  @Input() tabindex: number;
  @Input() placeholder = '';
  @Input()
  set disabled(value) {
    this._disabled = value;
  }
  get disabled() {
    return this._disabled || false;
  }
  @Input()
  @HostBinding('class.readonly')
  set readonly(value) {
    this._readonly = value;
  }
  get readonly() {
    return this._readonly;
  }

  protected propagateChange = identity;
  protected propagateTouched = identity;
  protected _disabled: boolean;
  protected _readonly: boolean;

  /**
   * Writes a new value to the element.
   * This method will be called by the forms API to write to the view when programmatic (model -> view) changes are
   * requested.
   *
   * Angular will call this method with the value in one of the following cases:
   *  1. When you instantiate a new FormControl
   *  2. When you call control.patchValue or control.setValue
   * */
  abstract writeValue(obj: any): void;

  /**
   * Registers a callback function that should be called when the control's value changes in the UI.
   * This is called by the forms API on initialization so it can update the form model when values propagate from the
   * view (view -> model).
   * */
  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  /**
   * Registers a callback function that should be called when the control receives a blur event.
   * This is called by the forms API on initialization so it can update the form model on blur.
   * */
  registerOnTouched(fn: any): void {
    this.propagateTouched = fn;
  }

  /**
   * This method is called by the forms API when the control status changes to or from "DISABLED". Depending on the
   * value, it will enable or disable the appropriate DOM element.
   *
   * Angular will call this method in one of the following cases:
   *  1. When you instantiate a new FormControl with the disabled property set to true.
   *  2. When you call control.disable() or when you call control.enable()
   * */
  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }
}
